// ─────────────────────────────────────────────────────────────
// store-sheets.jsx — Shared Store-Modals + Settings-Bausteine.
// Extrahiert aus app.jsx damit flea-Module diese importieren
// können ohne zirkuläre Abhängigkeiten zu app.jsx.
//
// NEUES Design-System: components/boothly/settings-ui.jsx
// Die Settings-Bausteine unten werden noch von ModeMembersSheet genutzt.
//
// Exports:
//   Settings-Bausteine:  SettingsSectionLabel, SettingsCard, SettingsRow
//   Store-Modals:        ConfirmDeleteStoreSheet, ModeMembersSheet,
//                        InviteCodeSheet, ConfirmDialog
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { F } from './theme.jsx';
import { useModalViewport, modalShellStyle, ModalHeader } from './modal-utils.jsx';
import { useToast } from './components/boothly/toast-provider.jsx';

// BottomSheet wird lazy importiert um Circular-Dep zu vermeiden —
// es liegt in widgets.jsx das seinerseits nichts aus store-sheets importiert.
// Da store-sheets.jsx NACH widgets.jsx im Modul-Graphen steht, ist der
// direkte Import sicher.
import { BottomSheet } from './widgets.jsx';

// ─────────────────────────────────────────────────────────────
// Settings-Bausteine
// ─────────────────────────────────────────────────────────────

export function SettingsSectionLabel({ children, t }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 600, color: t.textMuted,
      letterSpacing: "0.06em", textTransform: "uppercase",
      paddingLeft: 32, paddingRight: 16,
      paddingTop: 24, paddingBottom: 8,
    }}>{children}</div>
  );
}

export function SettingsCard({ children, dark, t }) {
  return (
    <div
      className="glass-section"
      style={{
        marginLeft: 16, marginRight: 16,
        background: dark ? "rgba(28,28,30,0.78)" : "rgba(255,255,255,0.78)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >{children}</div>
  );
}

export function SettingsRow({ label, value, sub, onClick, destructive, isFirst, t, dark, F: _F }) {
  return (
    <button onClick={onClick} disabled={!onClick} style={{
      boxSizing: "border-box",
      width: "100%", padding: "14px 16px",
      background: "transparent", border: "none",
      color: destructive ? t.accent.red.solid : t.text,
      fontFamily: _F || F, fontWeight: 400, fontSize: 15,
      cursor: onClick ? "pointer" : "default", textAlign: "left",
      display: "flex", alignItems: "center", gap: 12,
      borderTop: isFirst ? "none" : `0.5px solid ${t.divider}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: t.textSub, marginTop: 2, fontWeight: 400 }}>{sub}</div>}
      </div>
      {value !== undefined && <div style={{ color: destructive ? t.accent.red.solid : t.textMuted, fontSize: 14, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{value}</div>}
      {onClick && !destructive && <div style={{ fontSize: 18, color: t.chevron, fontWeight: 300, flexShrink: 0 }}>›</div>}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Store-Modals
// ─────────────────────────────────────────────────────────────

export function ConfirmDeleteStoreSheet({ dark, t, storeName, storeType, onCancel, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const expected = "LÖSCHEN";
  const ready = confirmText.trim().toUpperCase() === expected;
  const typeLabel = storeType === "flea" ? "Trödel-Bereich" : "Online-Store";

  return (
    <>
      <div onClick={onCancel} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.65)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 420, radius: 24, padding: "22px 22px 18px", zIndex: 97 })}>
        <ModalHeader t={t} dark={dark} F={F} title={`${typeLabel} löschen`} subtitle={storeName} onClose={onCancel} marginBottom={14} />
        <div style={{ background: t.accent.red.weak, color: t.accent.red.solid, borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>
          Alle Daten dieses Bereichs werden permanent gelöscht: Bestellungen, Items, Markt-Tage, Verkäufe, Mitgliedschaften. Diese Aktion ist unwiderruflich.
        </div>
        <div style={{ fontSize: 12, color: t.textSub, marginBottom: 6, paddingLeft: 4 }}>
          Tippe <b style={{ color: t.text }}>{expected}</b> zum Bestätigen:
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <input autoFocus type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0, letterSpacing: "0.04em" }}/>
        </div>
        <button onClick={async () => { if (!ready || submitting) return; setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={!ready || submitting} style={{
          width: "100%", padding: "13px",
          background: ready ? t.accent.red.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
          color: ready ? "#fff" : t.textMuted,
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: ready && !submitting ? "pointer" : "not-allowed",
          marginBottom: 8,
        }}>{submitting ? "Lösche…" : "Endgültig löschen"}</button>
        <button onClick={onCancel} style={{
          width: "100%", padding: "10px",
          background: "transparent", border: "none",
          color: t.textMuted, fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Abbrechen</button>
      </div>
    </>
  );
}

export function InviteCodeSheet({ dark, t, code, expiresAt, storeName, onClose }) {
  const toast = useToast();
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [copied, setCopied] = useState(false);
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const daysLeft = expiresDate ? Math.max(0, Math.ceil((expiresDate.getTime() - Date.now()) / 86400000)) : 7;

  const handleShare = async () => {
    const shareText = `Tritt meinem ${storeName || "Boothly-Bereich"} bei mit dem Code: ${code}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Boothly-Einladung", text: shareText }); return; }
      catch (e) { /* fallthrough */ }
    }
    handleCopy();
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.2s ease",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 22, padding: "22px 22px 18px", zIndex: 97 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Code generiert" onClose={onClose} marginBottom={20} />
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", border: `1px solid ${t.glassBorder}`, borderRadius: 14, padding: "20px 16px", marginBottom: 14, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: t.text, letterSpacing: "0.10em", fontVariantNumeric: "tabular-nums", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{code}</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8 }}>Gültig bis: in {daysLeft} {daysLeft === 1 ? "Tag" : "Tagen"}</div>
        </div>
        <div style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5, marginBottom: 16, textAlign: "center" }}>Teile diesen Code mit der Person, die deinem Bereich beitreten soll.</div>
        <button onClick={handleShare} style={{ width: "100%", padding: "13px", background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 12, fontFamily: F, fontWeight: 700, fontSize: 14, cursor: "pointer", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          Code teilen
        </button>
        <button onClick={handleCopy} style={{ width: "100%", padding: "11px", background: "transparent", border: `1px solid ${t.glassBorder}`, borderRadius: 12, color: t.text, fontFamily: F, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          {copied ? "Code kopiert ✓" : "Code kopieren"}
        </button>
      </div>
    </>
  );
}

export function ConfirmDialog({ dark, t, title, message, confirmLabel, confirmDestructive, onCancel, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useState(false);
  const accent = confirmDestructive ? t.accent.red : t.accent.blue;
  return (
    <>
      <div onClick={onCancel} style={{ position: "fixed", inset: 0, zIndex: 97, background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 340, radius: 22, padding: "22px 22px 16px", zIndex: 98 })}>
        <div style={{ fontSize: 17, fontWeight: 700, color: t.text, marginBottom: 6, textAlign: "center" }}>{title}</div>
        {message && <div style={{ fontSize: 13, color: t.textSub, marginBottom: 18, textAlign: "center", lineHeight: 1.4 }}>{message}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "11px", background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", border: "none", borderRadius: 11, color: t.text, fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Abbrechen</button>
          <button onClick={async () => { setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={submitting} style={{ flex: 1, padding: "11px", background: accent.solid, color: "#fff", border: "none", borderRadius: 11, fontFamily: F, fontWeight: 700, fontSize: 14, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}>{submitting ? "…" : confirmLabel}</button>
        </div>
      </div>
    </>
  );
}

export function ModeMembersSheet({ isOpen, onClose, store, authToken, dark, t, onMembersChanged }) {
  const toast = useToast();
  const [members, setMembers]         = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [showCode, setShowCode]       = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadMembersAndInvitations = async () => {
    if (!store?.id || !authToken) return;
    setLoading(true);
    const headers = { "Authorization": `Bearer ${authToken}` };
    try {
      const [mr, ir] = await Promise.all([
        fetch(`/api/stores/${store.id}/members`, { headers }),
        fetch(`/api/stores/${store.id}/invitations`, { headers }),
      ]);
      if (mr.ok) setMembers(await mr.json()); else setMembers([]);
      if (ir.ok) setInvitations(await ir.json()); else setInvitations([]);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isOpen) loadMembersAndInvitations(); }, [isOpen, store?.id]);

  const generateCode = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await fetch(`/api/stores/${store.id}/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${authToken}` },
        body: JSON.stringify({ role: "fulfillment" }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.reason || "Code konnte nicht generiert werden"); return; }
      const inv = await res.json();
      setShowCode({ code: inv.code, role: inv.role, expires_at: inv.expires_at });
      loadMembersAndInvitations();
    } catch (e) { toast.error("Netzwerkfehler"); }
    finally { setGenerating(false); }
  };

  const removeMember = async (member) => {
    try {
      const res = await fetch(`/api/stores/${store.id}/members/${member.user_id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.reason || "Entfernen fehlgeschlagen"); return; }
      toast.success("Mitglied entfernt");
      loadMembersAndInvitations();
      if (onMembersChanged) onMembersChanged();
    } catch (e) { toast.error("Netzwerkfehler"); }
  };

  const revokeInvite = async (invId) => {
    try {
      const res = await fetch(`/api/stores/${store.id}/invitations/${invId}`, { method: "DELETE", headers: { "Authorization": `Bearer ${authToken}` } });
      if (!res.ok) { toast.error("Code konnte nicht widerrufen werden"); return; }
      toast.success("Code widerrufen");
      loadMembersAndInvitations();
    } catch (e) { toast.error("Netzwerkfehler"); }
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Mitglieder" subtitle={store?.name} dark={dark} t={t} F={F}>
        <SettingsSectionLabel t={t}>Mitglieder ({members.length})</SettingsSectionLabel>
        <SettingsCard dark={dark} t={t}>
          {members.length === 0 ? (
            <SettingsRow isFirst label={loading ? "Lade…" : "Nur du"} t={t} dark={dark} F={F} />
          ) : members.map((m, i) => (
            <SettingsRow key={m.user_id} isFirst={i === 0} label={m.email || m.user_id} sub={m.role === "owner" ? "Inhaber" : m.role} value={m.role !== "owner" ? "Entfernen" : undefined} onClick={m.role !== "owner" ? () => setConfirmRemove(m) : undefined} destructive={m.role !== "owner"} t={t} dark={dark} F={F} />
          ))}
        </SettingsCard>
        {invitations.length > 0 && (
          <>
            <SettingsSectionLabel t={t}>Aktive Codes ({invitations.length})</SettingsSectionLabel>
            <SettingsCard dark={dark} t={t}>
              {invitations.map((inv, i) => (
                <SettingsRow key={inv.id} isFirst={i === 0} label={inv.code} sub={`Rolle: ${inv.role} · gültig bis ${new Date(inv.expires_at).toLocaleDateString("de-DE")}`} value="Widerrufen" onClick={() => revokeInvite(inv.id)} destructive t={t} dark={dark} F={F} />
              ))}
            </SettingsCard>
          </>
        )}
        <div style={{ padding: "20px 16px 8px" }}>
          <button onClick={generateCode} disabled={generating} style={{ width: "100%", padding: "14px", background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 12, fontFamily: F, fontWeight: 700, fontSize: 15, cursor: generating ? "default" : "pointer", opacity: generating ? 0.6 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: `0 4px 14px ${t.accent.blue.weak}` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            {generating ? "Erzeuge…" : "Mitglied einladen"}
          </button>
          <div style={{ fontSize: 11, color: t.textSub, marginTop: 8, textAlign: "center", lineHeight: 1.4 }}>Generiert einen Einladungscode, gültig 7 Tage</div>
        </div>
      </BottomSheet>
      {showCode && <InviteCodeSheet dark={dark} t={t} code={showCode.code} expiresAt={showCode.expires_at} storeName={store?.name} onClose={() => setShowCode(null)} />}
      {confirmRemove && <ConfirmDialog dark={dark} t={t} title={`${confirmRemove.email || confirmRemove.user_id} entfernen?`} message="Das Mitglied verliert sofort Zugriff auf diesen Bereich." confirmLabel="Entfernen" confirmDestructive onCancel={() => setConfirmRemove(null)} onConfirm={async () => { await removeMember(confirmRemove); setConfirmRemove(null); }} />}
    </>
  );
}
